'use client';

import { PostHogEvent } from '@/lib/analytics/posthog-events';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { cn } from '@/lib/utils';
import { useAction, useMutation } from '@/lib/convex';
import { api } from '@/lib/convex';
import {
  cleanupContactUploadClaims,
  CONTACT_IMAGE_ACCEPT,
  CONTACT_UPLOAD_MAX_BYTES,
  uploadContactAttachmentBatch,
} from '@/lib/contact/contact-uploads';
import { useState, useRef, useEffect } from 'react';
import { ArrowUp, X, Paperclip } from 'lucide-react';
import { motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { usePostHog } from 'posthog-js/react';
import type { ContactAttachment } from '../pricing/pricing-contact-attachments';

// Generate a session ID for rate limiting
function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sessionId = sessionStorage.getItem('contactSessionId');
  if (!sessionId) {
    sessionId = `contact_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem('contactSessionId', sessionId);
  }
  return sessionId;
}

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactClient() {
  const t = useTranslations('MarketingUI');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isMessageFocused, setIsMessageFocused] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<ContactAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const generateUploadUrl = useMutation(
    api.contact.mutations.generateUploadUrl
  );
  const cleanupContactUploads = useAction(
    api.contact.actions.cleanupContactUploads
  );
  const posthog = usePostHog();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [message]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed the limit
    const currentCount = attachments.length;
    if (currentCount + files.length > 5) {
      setError(t('contactUploadLimit'));
      e.target.value = ''; // Reset input
      return;
    }

    // Validate all files
    const validFiles: File[] = [];
    for (const file of files) {
      // Validate file type (images only)
      if (!CONTACT_IMAGE_ACCEPT.split(',').includes(file.type)) {
        setError(t('contactImageOnly'));
        e.target.value = ''; // Reset input
        return;
      }

      if (file.size > CONTACT_UPLOAD_MAX_BYTES) {
        setError(t('contactFileTooLarge'));
        e.target.value = ''; // Reset input
        return;
      }

      validFiles.push(file);
    }

    setError(null);

    // Create preview URLs and add to attachments
    const newAttachments = validFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);

    // Upload all files to Convex storage
    try {
      setIsUploading(true);
      const results = await uploadContactAttachmentBatch({
        attachments: newAttachments,
        sessionId: getSessionId(),
        generateUploadUrl,
        cleanup: cleanupContactUploads,
      });
      const uploadsById = new Map(results.map((result) => [result.id, result]));
      setAttachments((previous) =>
        previous.map((attachment) => ({
          ...attachment,
          ...uploadsById.get(attachment.id),
        }))
      );
      for (const attachment of newAttachments) {
        posthog?.capture(PostHogEvent.FILE_UPLOADED, {
          filename: attachment.file.name,
          mimeType: attachment.file.type,
          sizeBytes: attachment.file.size,
          source: 'contact_form',
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(t('contactUploadFailed'));
      const failedIds = new Set(newAttachments.map(({ id }) => id));
      newAttachments.forEach(({ previewUrl }) =>
        URL.revokeObjectURL(previewUrl)
      );
      setAttachments((previous) =>
        previous.filter(({ id }) => !failedIds.has(id))
      );
    } finally {
      setIsUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleRemoveAttachment = (index: number) => {
    const attachmentToRemove = attachments[index];
    if (attachmentToRemove?.previewUrl) {
      URL.revokeObjectURL(attachmentToRemove.previewUrl);
    }
    if (attachmentToRemove?.storageKey && attachmentToRemove.uploadToken) {
      void cleanupContactUploadClaims({
        sessionId: getSessionId(),
        claims: [
          {
            storageKey: attachmentToRemove.storageKey,
            uploadToken: attachmentToRemove.uploadToken,
          },
        ],
        cleanup: cleanupContactUploads,
      });
    }
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate email
    if (!email.trim()) {
      setError(t('contactEmailRequired'));
      return;
    }

    if (!emailRegex.test(email)) {
      setError(t('contactEmailInvalid'));
      return;
    }

    // Validate message
    if (!message.trim()) {
      setError(t('contactMessageRequired'));
      return;
    }

    if (isSubmitting || isUploading) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const sessionId = getSessionId();
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim(),
          attachments: attachments
            .filter((att) => att.storageKey && att.uploadToken)
            .map((att) => ({
              storageKey: att.storageKey,
              uploadToken: att.uploadToken,
              filename: att.file.name,
            })),
          sessionId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'RATE_LIMIT_EXCEEDED') {
          setError(data.message || t('contactRateLimit'));
        } else if (data.error === 'INVALID_EMAIL') {
          setError(data.message || t('contactEmailInvalid'));
        } else {
          setError(data.message || t('contactSubmitFailed'));
        }
        return;
      }

      setSubmitted(true);
      setMessage('');
      setEmail('');
      attachments.forEach((att) => {
        if (att.previewUrl) {
          URL.revokeObjectURL(att.previewUrl);
        }
      });
      setAttachments([]);
    } catch (err: unknown) {
      console.error('Contact form submission error:', err);
      setError(t('contactSubmitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = emailRegex.test(email) && message.trim().length > 0;

  if (submitted) {
    return (
      <main className="bg-background relative flex min-h-screen items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md text-center"
        >
          <h1 className="text-foreground mb-4 text-3xl font-light">
            {t('contactThankYou')}
          </h1>
          <p className="text-muted-foreground">{t('contactFollowUp')}</p>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="bg-background relative flex min-h-screen items-center justify-center px-4 py-12">
      <div className="relative z-10 w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="text-center">
            <h1 className="text-foreground text-3xl font-light">
              {t('contactTitle')}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {t('contactSubtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email input */}
            <div className="space-y-2">
              <div
                className={cn(
                  'bg-card/40 border-border/30 rounded-3xl border p-2 shadow-sm backdrop-blur-sm transition-all duration-200',
                  isEmailFocused
                    ? 'border-primary/50 ring-primary/10 bg-card/60 ring-2'
                    : ''
                )}
              >
                <div className="relative flex items-center">
                  <input
                    ref={emailInputRef}
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    onFocus={() => setIsEmailFocused(true)}
                    onBlur={() => setIsEmailFocused(false)}
                    placeholder={t('contactEmailPlaceholder')}
                    className="text-foreground placeholder-muted-foreground/70 flex-1 bg-transparent pl-2.5 text-[16px] leading-relaxed [caret-color:hsl(var(--primary))] outline-none [caret-width:2px]"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Message textarea */}
            <div className="space-y-2">
              <div
                className={cn(
                  'bg-card/40 border-border/30 rounded-3xl border shadow-sm backdrop-blur-sm transition-all duration-200',
                  isMessageFocused
                    ? 'border-primary/50 ring-primary/10 bg-card/60 ring-2'
                    : ''
                )}
              >
                <div className="p-2">
                  <textarea
                    ref={textareaRef}
                    id="message"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onFocus={() => setIsMessageFocused(true)}
                    onBlur={() => setIsMessageFocused(false)}
                    placeholder={t('contactMessagePlaceholder')}
                    className="text-foreground placeholder-muted-foreground/70 min-h-[120px] w-full resize-none bg-transparent pl-2.5 text-[16px] leading-relaxed [caret-color:hsl(var(--primary))] outline-none [caret-width:2px]"
                    required
                  />
                </div>

                {/* Icons container */}
                <div className="flex items-center justify-between px-2 pb-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 rounded-full"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || attachments.length >= 5}
                    aria-label={t('contactUploadImages')}
                  >
                    {isUploading ? (
                      <Loader size="sm" aria-label={t('contactUploadImages')} />
                    ) : (
                      <Paperclip className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    type="submit"
                    size="icon"
                    className="h-6 w-6 rounded-full"
                    disabled={!isValid || isSubmitting || isUploading}
                    aria-label={t('contactSend')}
                  >
                    {isSubmitting ? (
                      <Loader size="sm" aria-label={t('contactSend')} />
                    ) : (
                      <ArrowUp className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Attachments preview */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={attachment.id}
                    className="border-border/30 bg-card/40 flex items-center gap-2 rounded-lg border p-2 backdrop-blur-sm"
                  >
                    {attachment.previewUrl && (
                      <img
                        src={attachment.previewUrl}
                        alt={attachment.file.name}
                        className="h-10 w-10 rounded object-cover"
                      />
                    )}
                    <div className="text-foreground flex-1 truncate text-sm">
                      {attachment.file.name}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={() => handleRemoveAttachment(index)}
                      aria-label={t('contactRemoveAttachment')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="border-destructive/50 bg-destructive/10 text-destructive rounded-lg border p-3 text-sm">
                {error}
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={CONTACT_IMAGE_ACCEPT}
              className="hidden"
              onChange={handleFileSelect}
              multiple
            />
          </form>
        </motion.div>
      </div>
    </main>
  );
}
